import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { MapPin } from "lucide-react";

// Fix für fehlende Standard-Marker-Icons im Webpack-Bundle
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const STATUS_COLORS = {
  neu: "#2563eb",
  vorgemerkt: "#7c3aed",
  kontaktiert: "#0891b2",
  qualifiziert: "#16a34a",
  kein_interesse: "#6b7280",
  ausgeschlossen: "#dc2626",
};

function coloredIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -18],
  });
}

// Suchmittelpunkt-Icon (Fadenkreuz)
const centerIcon = L.divIcon({
  className: "",
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#ea580c;border:3px solid #fff;box-shadow:0 0 0 2px #ea580c,0 1px 6px rgba(0,0,0,0.5)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -12],
});

const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function popupHtml(l) {
  const rows = [`<div style="font-weight:600">${esc(l.practice_name || "Unbenannte Praxis")}</div>`];
  if (l.street) rows.push(`<div>${esc(l.street)}</div>`);
  const zc = [l.zip, l.city].filter(Boolean).join(" ");
  if (zc) rows.push(`<div>${esc(zc)}</div>`);
  if (l.phone) rows.push(`<a href="tel:${esc(l.phone)}" style="color:#2563eb;display:block">${esc(l.phone)}</a>`);
  if (l.email)
    rows.push(`<a href="mailto:${esc(l.email)}" style="color:#2563eb;display:block;word-break:break-all">${esc(l.email)}</a>`);
  if (l.website)
    rows.push(
      `<a href="${esc(l.website)}" target="_blank" rel="noreferrer" style="color:#2563eb;display:block;word-break:break-all">${esc(l.website)}</a>`
    );
  rows.push(
    `<div style="font-size:11px;color:#6b7280;padding-top:4px">Status: ${esc(l.status)} · Score ${esc(l.lead_score)}</div>`
  );
  const origin = [];
  if (l.source_location) origin.push(`Herkunft: ${esc(l.source_location)}`);
  if (l.source_radius_km != null) origin.push(`Umkreis ${esc(l.source_radius_km)} km`);
  if (l.distance_km != null) origin.push(`${esc(l.distance_km)} km entfernt`);
  if (origin.length)
    rows.push(`<div style="font-size:11px;color:#6b7280" data-testid="map-popup-radius-${esc(l.id)}">${origin.join(" · ")}</div>`);
  return `<div data-testid="map-popup-${esc(l.id)}" style="font-size:13px;line-height:1.35">${rows.join("")}</div>`;
}

// Cluster-Ebene: bündelt dichte Bereiche zu Zahlen-Clustern (leaflet.markercluster).
function ClusterLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    const group = L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 55, showCoverageOnHover: false });
    points.forEach((l) => {
      const m = L.marker([l.lat, l.lon], { icon: coloredIcon(STATUS_COLORS[l.status] || "#2563eb") });
      m.bindPopup(popupHtml(l));
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, points]);
  return null;
}

// Passt den Kartenausschnitt an alle Pins + Suchradien an.
function FitBounds({ points, searches }) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    searches.forEach((s) => {
      const c = L.latLng(s.lat, s.lon);
      bounds.extend(c.toBounds((s.radius_km || 1) * 2000));
    });
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
  }, [map, points, searches]);
  return null;
}

export function LeadsMap({ leads, searches = [] }) {
  const points = useMemo(
    () => (Array.isArray(leads) ? leads : []).filter((l) => typeof l.lat === "number" && typeof l.lon === "number"),
    [leads]
  );

  const centers = useMemo(
    () =>
      (Array.isArray(searches) ? searches : []).filter(
        (s) => typeof s.lat === "number" && typeof s.lon === "number"
      ),
    [searches]
  );

  const initialCenter = useMemo(() => {
    if (centers.length) return [centers[0].lat, centers[0].lon];
    if (points.length) {
      const lat = points.reduce((s, l) => s + l.lat, 0) / points.length;
      const lon = points.reduce((s, l) => s + l.lon, 0) / points.length;
      return [lat, lon];
    }
    return [50.1109, 8.6821]; // Fallback: Frankfurt/Offenbach
  }, [points, centers]);

  if (points.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground" data-testid="leads-map-empty">
        <MapPin className="h-6 w-6 mx-auto mb-2 opacity-60" />
        Keine Praxen mit Koordinaten vorhanden. Führe zuerst eine Suche durch.
      </div>
    );
  }

  return (
    <div className="h-[600px] w-full" data-testid="leads-map">
      <MapContainer center={initialCenter} zoom={11} scrollWheelZoom className="h-full w-full rounded-lg" style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Radius-Overlay je Suchmittelpunkt */}
        {centers.map((s, i) => (
          <Circle
            key={`radius-${i}`}
            center={[s.lat, s.lon]}
            radius={(s.radius_km || 0) * 1000}
            pathOptions={{ color: "#ea580c", weight: 1.5, fillColor: "#ea580c", fillOpacity: 0.06 }}
            data-testid={`map-radius-${i}`}
          />
        ))}
        {centers.map((s, i) => (
          <Marker key={`center-${i}`} position={[s.lat, s.lon]} icon={centerIcon}>
            <Popup>
              <div className="text-sm" data-testid={`map-center-${i}`}>
                <div className="font-semibold">Suchmittelpunkt</div>
                {s.location && <div>{s.location}</div>}
                <div className="text-xs text-muted-foreground">Radius: {s.radius_km} km</div>
              </div>
            </Popup>
          </Marker>
        ))}

        <ClusterLayer points={points} />
        <FitBounds points={points} searches={centers} />
      </MapContainer>
    </div>
  );
}
